import { computed } from 'vue'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import AccountType from '@/utils/AccountType'

const escapeHtml = (text: string): string => {
  const div = document.createElement('div')
  div.textContent = text
  return div.innerHTML
}

/**
 * Generates AI chat welcome messages based on account type.
 * Uses impersonator's name when impersonating.
 */
export function useAiWelcomeMessage() {
  const isImpersonating = computed(
    () => typeof currentImpersonator !== 'undefined' && currentImpersonator !== null,
  )
  const userName = computed(() => {
    const name = isImpersonating.value
      ? (currentImpersonator?.name ?? currentUser?.name ?? 'there')
      : (currentUser?.name ?? 'there')
    return escapeHtml(name)
  })
  const accountType = computed(() => currentAccountType ?? 'freelancer')

  const getWelcomeGreeting = computed(() => $t('Welcome, :name! How can I assist you today?', { name: userName.value }))

  const getHelpTopics = computed((): string[] => {
    if (AccountType.isCompany(accountType.value)) {
      return [
        $t('Understanding your payment responsibilities'),
        $t('Managing contract details and compliance'),
        $t('Insights on custom fields and other data on this hire'),
        $t('How the worker can request payments'),
        $t('How Worksome works'),
      ]
    }

    if (AccountType.isFreelancer(accountType.value) || AccountType.isWorker(accountType.value)) {
      return [
        $t('Understanding the steps to get ready to work'),
        $t('Submitting a payment request'),
        $t('Status of pending payments'),
        $t('Understanding contract terms and classifications'),
        $t('How Worksome works'),
      ]
    }

    if (AccountType.isRecruiter(accountType.value)) {
      return [
        $t('Understanding the hiring process'),
        $t('Managing candidates and placements'),
        $t('Contract and compliance questions'),
        $t('How Worksome works'),
      ]
    }

    return [
      $t('Getting started with Worksome'),
      $t('Understanding contracts and compliance'),
      $t('How Worksome works'),
    ]
  })

  const getWelcomeMessage = computed(() => {
    const greeting = getWelcomeGreeting.value
    const helpIntro = $t('Here are a few things I can help with:')
    const topicsList = getHelpTopics.value.map((topic) => `<li>${topic}</li>`).join('')
    const closing = $t('Let me know how I can assist you!')

    return `<p>${greeting}</p><p>${helpIntro}</p><ul>${topicsList}</ul><p>${closing}</p>`
  })

  const createWelcomeMessage = () => ({
    id: crypto.randomUUID(),
    isAuthored: false,
    isPending: false,
    content: getWelcomeMessage.value,
    isWelcomeMessage: true,
  })

  return {
    userName,
    accountType,
    getWelcomeGreeting,
    getHelpTopics,
    getWelcomeMessage,
    createWelcomeMessage,
  }
}

export default useAiWelcomeMessage
